import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from '../hooks/use-toast';
import { ArrowLeft, ShoppingCart, Plus, Minus, Send, Flame, Droplets, UtensilsCrossed, Loader2, Wine, User, Trash2, Beer } from 'lucide-react';
import api from '../api/axios';
import { getMenuCategories } from '../api/api';

const StaffOrder = () => {
  const navigate = useNavigate();
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState('');
  const [menuCategories, setMenuCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [cart, setCart] = useState([]);
  const [customerName, setCustomerName] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [menuTab, setMenuTab] = useState('food');
  const [drinksWithFood, setDrinksWithFood] = useState(false);

  const foodCategories = menuCategories.filter(c => !c.isDrink);
  const drinkCategories = menuCategories.filter(c => c.isDrink);
  const visibleCategories = menuTab === 'food' ? foodCategories : drinkCategories;

  const handleMenuTabChange = (tab) => {
    setMenuTab(tab);
    const cats = tab === 'food' ? foodCategories : drinkCategories;
    if (cats.length > 0 && !cats.find(c => c.id === selectedCategory)) {
      setSelectedCategory(cats[0].id);
    }
  };

  // AI suggestions
  const [showOptions, setShowOptions] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [suggestions, setSuggestions] = useState(null);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [selectedCuisson, setSelectedCuisson] = useState(null);
  const [selectedSauces, setSelectedSauces] = useState([]);
  const [selectedAccomp, setSelectedAccomp] = useState([]);
  // Wine color picker
  const [showWinePicker, setShowWinePicker] = useState(false);
  const [pendingWineItem, setPendingWineItem] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('staffToken');
    if (!token) { navigate('/staff/login'); return; }
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    try {
      const [tablesRes, menuData, drinksRes] = await Promise.all([
        api.get('/tables'),
        getMenuCategories(),
        api.get('/menu/drinks').then(r => r.data).catch(() => [])
      ]);
      setTables(tablesRes.data.filter(t => t.active));
      const drinkCats = drinksRes.map(c => ({ ...c, isDrink: true }));
      setMenuCategories([...menuData, ...drinkCats]);
      if (menuData.length > 0) setSelectedCategory(menuData[0].id);
    } catch (e) {
      toast({ title: 'Erreur chargement', variant: 'destructive' });
    }
  };

  const isWineGlass = (item, cat) => {
    const n = (item.name || '').toLowerCase();
    const cid = (cat?.id || '').toLowerCase();
    return n.includes('verre') || (cid.startsWith('vins') && (n.includes('pichet') || n.includes('verre')));
  };

  const handleAddItem = async (item) => {
    const currentCat = menuCategories.find(c => c.id === selectedCategory);
    if (currentCat?.isDrink) {
      // Check if it's a wine glass that needs color selection
      if (isWineGlass(item, currentCat)) {
        setPendingWineItem(item);
        setShowWinePicker(true);
      } else {
        addToCartDirect(item, null, null, []);
      }
      return;
    }
    setCurrentItem(item);
    setSelectedCuisson(null);
    setSelectedSauces([]);
    setSelectedAccomp([]);
    setLoadingSuggestions(true);
    setShowOptions(true);
    try {
      const categoryName = currentCat?.name || '';
      const res = await api.post('/ai/suggestions', { item_name: item.name, category: categoryName, item_id: item.id });
      const data = res.data;
      if (data.needs_cuisson || data.needs_sauce || data.needs_accompagnement) {
        setSuggestions(data);
      } else {
        setShowOptions(false);
        addToCartDirect(item, null, null, []);
      }
    } catch (e) {
      setShowOptions(false);
      addToCartDirect(item, null, null, []);
    } finally { setLoadingSuggestions(false); }
  };

  const addToCartDirect = (item, cuisson, sauces, accomp, serveWithFood = false) => {
    const currentCat = menuCategories.find(c => c.id === selectedCategory);
    const itemType = currentCat?.isDrink ? 'boisson' : 'plat';
    const catName = currentCat?.name || '';
    const accompNames = (accomp || []).map(a => typeof a === 'object' ? a.name : a);
    const includedAccompaniments = suggestions?.included_accompaniments ?? 2;
    const extraAccompanimentFee = suggestions?.extra_accompaniment_fee ?? 5;
    const pricedAccompSupplement = (accomp || []).reduce((s, a) => s + (typeof a === 'object' && a.prix ? a.prix : 0), 0);
    const extraAccompSupplement = Math.max(0, accompNames.length - includedAccompaniments) * extraAccompanimentFee;
    const accompSupplement = pricedAccompSupplement + extraAccompSupplement;
    const basePrice = typeof item.price === 'number' ? item.price : parseFloat(item.price) || 0;
    const selectedSauceList = sauces || [];
    const sauceSupplement = selectedSauceList.length > 1 ? 5 : 0;
    const finalPrice = (basePrice + accompSupplement + sauceSupplement).toFixed(2);
    setCart(prev => {
      const key = `${item.id}-${cuisson || ''}-${selectedSauceList.join(',')}-${accompNames.join(',')}-${serveWithFood ? 'avec-plats' : 'direct'}`;
      const existing = prev.find(i => i._key === key);
      if (existing) return prev.map(i => i._key === key ? { ...i, quantity: i.quantity + 1 } : i);
      return [...prev, {
        _key: key, menu_item_id: item.id, name: item.name,
        price: finalPrice,
        quantity: 1, cuisson, sauces: selectedSauceList,
        sauce: selectedSauceList.join(', ') || null,
        base_price: basePrice.toFixed(2),
        accompagnement_supplement: accompSupplement,
        extra_accompaniment_supplement: extraAccompSupplement,
        sauce_supplement: sauceSupplement,
        accompagnements: accompNames, notes: '',
        type: itemType, category_name: catName, serve_with_food: serveWithFood
      }];
    });
    toast({ title: 'Ajoute', description: item.name });
  };

  const confirmOptions = () => {
    if (currentItem) addToCartDirect(currentItem, selectedCuisson, selectedSauces, selectedAccomp);
    setShowOptions(false);
    setSuggestions(null);
  };

  const updateQuantity = (key, delta) => {
    setCart(prev => prev.map(item => {
      if (item._key === key) {
        const nq = item.quantity + delta;
        return nq > 0 ? { ...item, quantity: nq } : item;
      }
      return item;
    }).filter(item => item.quantity > 0));
  };

  const toggleDrinkTiming = (key) => {
    setCart(prev => {
      const next = prev.map(item => item._key === key
        ? { ...item, serve_with_food: !item.serve_with_food }
        : item);
      const drinks = next.filter(item => item.type === 'boisson');
      setDrinksWithFood(drinks.length > 0 && drinks.every(item => item.serve_with_food));
      return next;
    });
  };

  const removeItem = (key) => setCart(prev => prev.filter(i => i._key !== key));

  const total = cart.reduce((s, i) => s + parseFloat(i.price) * i.quantity, 0).toFixed(2);

  const hasDrinks = cart.some(i => i.type === 'boisson');
  const hasFood = cart.some(i => i.type !== 'boisson');

  const handleSubmit = async () => {
    if (!selectedTable) { toast({ title: 'Selectionnez une table', variant: 'destructive' }); return; }
    if (cart.length === 0) { toast({ title: 'Panier vide', variant: 'destructive' }); return; }
    if (!customerName.trim()) { toast({ title: 'Nom du client requis', variant: 'destructive' }); return; }
    setSubmitting(true);
    try {
      const items = cart.map(({ _key, ...rest }) => rest);
      await api.post('/orders/staff', {
        table_number: parseInt(selectedTable),
        table_id: `STAFF-${selectedTable}`,
        customer_name: customerName.trim(),
        items, total, notes,
        drinks_with_food: cart.some(item => item.type === 'boisson' && item.serve_with_food)
      });
      toast({ title: 'Commande envoyee !', description: `Table ${selectedTable} - ${customerName}` });
      setCart([]);
      setNotes('');
      setCustomerName('');
      setDrinksWithFood(false);
    } catch (e) {
      toast({ title: 'Erreur', description: e.response?.data?.detail || 'Erreur', variant: 'destructive' });
    } finally { setSubmitting(false); }
  };

  const currentCategory = menuCategories.find(c => c.id === selectedCategory);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f5f1ed] to-white" style={{ paddingBottom: cart.length > 0 ? '300px' : '0' }}>
      {/* Header */}
      <div className="bg-[#3d2817] text-white py-3 shadow-lg sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 flex items-center gap-4">
          <Button variant="ghost" className="text-white hover:bg-white/10" onClick={() => navigate('/staff/dashboard')} data-testid="back-btn">
            <ArrowLeft size={20} />
          </Button>
          <div className="flex-1">
            <h1 className="text-lg font-bold">Prise de Commande</h1>
            <p className="text-xs text-gray-300">Commander pour un client</p>
          </div>
          {cart.length > 0 && (
            <Badge className="bg-amber-500 text-white text-sm px-3">{cart.length} article{cart.length > 1 ? 's' : ''} - {total} EUR</Badge>
          )}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-4">
        {/* Table & Client Selection */}
        <Card className="mb-4 border-2 border-[#d4c4b0]" data-testid="order-header">
          <CardContent className="py-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1 block">Table</label>
                <Select value={selectedTable} onValueChange={setSelectedTable}>
                  <SelectTrigger data-testid="select-table"><SelectValue placeholder="Choisir une table..." /></SelectTrigger>
                  <SelectContent>
                    {tables.map(t => (
                      <SelectItem key={t.table_number} value={String(t.table_number)}>Table {t.table_number} ({t.covers} couverts)</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1 block">Nom du client</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                  <Input value={customerName} onChange={e => setCustomerName(e.target.value)}
                    placeholder="Nom du client" className="pl-10" data-testid="input-customer" />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Plats / Boissons Tabs */}
        <div className="flex gap-2 mb-3" data-testid="menu-tabs">
          <button onClick={() => handleMenuTabChange('food')} data-testid="staff-tab-food"
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-all flex items-center gap-1.5
              ${menuTab === 'food' ? 'bg-[#8B0000] text-white shadow-lg' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`}>
            <UtensilsCrossed size={14} /> Plats & Entrees
          </button>
          <button onClick={() => handleMenuTabChange('drinks')} data-testid="staff-tab-drinks"
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-all flex items-center gap-1.5
              ${menuTab === 'drinks' ? 'bg-amber-600 text-white shadow-lg' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`}>
            <Wine size={14} /> Boissons
          </button>
        </div>

        {/* Categories */}
        <div className="flex gap-2 overflow-x-auto pb-3 mb-3 scrollbar-hide">
          {visibleCategories.map(cat => (
            <button key={cat.id} onClick={() => setSelectedCategory(cat.id)} data-testid={`cat-${cat.id}`}
              className={`whitespace-nowrap px-3 py-2 rounded-full text-xs font-semibold transition-all flex items-center gap-1.5
                ${selectedCategory === cat.id ? (cat.isDrink ? 'bg-amber-600 text-white shadow-lg' : 'bg-[#8B0000] text-white shadow-lg') : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`}>
              {cat.isDrink && <Wine size={12} />}
              {cat.name.replace('Nos ', '')}
            </button>
          ))}
        </div>

        {/* Disclaimer Banner */}
        <div className="mb-3 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-start gap-2" data-testid="staff-menu-disclaimer-banner">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-amber-500 mt-0.5 shrink-0"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          <p className="text-[11px] text-amber-700 leading-relaxed">
            Les images sont donnees a titre indicatif et peuvent ne pas representer exactement le plat ou la boisson servi(e).
          </p>
        </div>

        {/* Menu Items */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {currentCategory?.items?.map(item => (
            <Card key={item.id} className="cursor-pointer hover:shadow-md transition-shadow border border-gray-200" data-testid={`item-${item.id}`}>
              <CardContent className="p-3 flex items-center gap-3" onClick={() => handleAddItem(item)}>
                {item.image && <img src={item.image} alt={item.name} className="w-14 h-14 rounded-lg object-cover flex-shrink-0" />}
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm truncate">{item.name}</p>
                  {item.description && <p className="text-[10px] text-gray-500 truncate">{item.description}</p>}
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="font-bold text-sm text-[#8B0000]">{item.price} EUR</p>
                  <Plus size={18} className="text-[#8B0000] ml-auto mt-1" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Floating Cart */}
      {cart.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-[#8B0000] shadow-2xl z-50 max-h-[300px] overflow-y-auto" data-testid="cart-panel">
          <div className="max-w-6xl mx-auto px-4 py-3">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-bold text-sm flex items-center gap-2"><ShoppingCart size={16} /> Panier ({cart.length})</h3>
              <span className="font-bold text-[#8B0000]">{total} EUR</span>
            </div>
            <div className="space-y-1.5 mb-3 max-h-[100px] overflow-y-auto">
              {cart.map(item => (
                <div key={item._key} className="flex items-center justify-between text-sm bg-gray-50 rounded-lg px-3 py-1.5">
                  <div className="flex-1 min-w-0">
                    <span className="font-medium truncate block">{item.name}</span>
                    {(item.cuisson || item.sauce || item.accompagnements?.length > 0) && (
                      <span className="text-[10px] text-gray-500">
                        {[item.cuisson, item.sauce, ...(item.accompagnements || [])].filter(Boolean).join(' | ')}
                      </span>
                    )}
                    {item.type === 'boisson' && (
                      <button onClick={() => toggleDrinkTiming(item._key)}
                        className={`mt-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded border ${item.serve_with_food ? 'bg-amber-100 text-amber-800 border-amber-300' : 'bg-blue-50 text-blue-700 border-blue-200'}`}>
                        {item.serve_with_food ? 'Avec les plats' : 'Service direct'}
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-2 ml-2">
                    <button onClick={() => updateQuantity(item._key, -1)} className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center"><Minus size={12} /></button>
                    <span className="font-bold text-sm w-5 text-center">{item.quantity}</span>
                    <button onClick={() => updateQuantity(item._key, 1)} className="w-6 h-6 rounded-full bg-[#8B0000] text-white flex items-center justify-center"><Plus size={12} /></button>
                    <button onClick={() => removeItem(item._key)} className="ml-1 text-red-400 hover:text-red-600"><Trash2 size={14} /></button>
                  </div>
                </div>
              ))}
            </div>

            {/* Drinks with food toggle - visible when cart has both drinks and food */}
            {hasDrinks && hasFood && (
              <button
                onClick={() => {
                  const next = !drinksWithFood;
                  setDrinksWithFood(next);
                  setCart(prev => prev.map(item => item.type === 'boisson' ? { ...item, serve_with_food: next } : item));
                }}
                className={`w-full mb-2 flex items-center justify-between px-3 py-2 rounded-lg border-2 transition-all text-sm font-semibold ${
                  drinksWithFood
                    ? 'bg-amber-50 border-amber-400 text-amber-800'
                    : 'bg-gray-50 border-gray-200 text-gray-600 hover:border-amber-300'
                }`}
                data-testid="drinks-with-food-toggle"
              >
                <span className="flex items-center gap-2">
                  <Beer size={16} className={drinksWithFood ? 'text-amber-600' : 'text-gray-400'} />
                  Envoyer les boissons avec les plats
                </span>
                <div className={`w-10 h-5 rounded-full transition-all relative ${drinksWithFood ? 'bg-amber-500' : 'bg-gray-300'}`}>
                  <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${drinksWithFood ? 'left-5' : 'left-0.5'}`} />
                </div>
              </button>
            )}

            <Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Notes speciales..." className="mb-2 h-10 text-xs" data-testid="order-notes" />
            <Button onClick={handleSubmit} disabled={submitting || !selectedTable || !customerName.trim()}
              className="w-full bg-[#8B0000] hover:bg-[#6d0000] text-white font-bold py-3" data-testid="submit-order-btn">
              {submitting ? <Loader2 className="animate-spin mr-2" size={16} /> : <Send size={16} className="mr-2" />}
              {submitting ? 'Envoi...' : `Envoyer la commande - ${total} EUR`}
            </Button>
          </div>
        </div>
      )}

      {/* AI Suggestions Dialog */}
      <Dialog open={showOptions} onOpenChange={o => { if (!o) { setShowOptions(false); setSuggestions(null); } }}>
        <DialogContent className="sm:max-w-md" data-testid="ai-suggestions-modal">
          <DialogHeader>
            <DialogTitle className="text-base">Options pour {currentItem?.name}</DialogTitle>
          </DialogHeader>
          {loadingSuggestions ? (
            <div className="flex items-center justify-center py-8"><Loader2 className="animate-spin text-[#8B0000]" size={32} /></div>
          ) : suggestions ? (
            <div className="space-y-4 py-2">
              {suggestions.needs_cuisson && (
                <div>
                  <p className="text-xs font-semibold text-gray-600 mb-2 flex items-center gap-1"><Flame size={14} className="text-red-500" /> Cuisson</p>
                  <div className="flex flex-wrap gap-2">
                    {suggestions.cuisson_options?.map(opt => (
                      <button key={opt} onClick={() => setSelectedCuisson(selectedCuisson === opt ? null : opt)}
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${selectedCuisson === opt ? 'bg-red-600 text-white border-red-600' : 'bg-white text-gray-700 border-gray-200 hover:border-red-300'}`}>
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {suggestions.needs_sauce && (
                <div>
                  <p className="text-xs font-semibold text-gray-600 mb-2 flex items-center gap-1"><Droplets size={14} className="text-amber-500" /> Sauce</p>
                  <div className="flex flex-wrap gap-2">
                    {suggestions.sauce_options?.map(opt => (
                      <button key={opt} onClick={() => setSelectedSauces(prev => prev.includes(opt) ? prev.filter(sauce => sauce !== opt) : [...prev, opt])}
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${selectedSauces.includes(opt) ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-gray-700 border-gray-200 hover:border-amber-300'}`}>
                        {opt}
                      </button>
                    ))}
                  </div>
                  <p className="text-[10px] text-gray-500 mt-1">Une sauce incluse ; plusieurs sauces entraînent un supplément.</p>
                  {selectedSauces.length > 1 && (
                    <div className="mt-2 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">
                      Supplément multi-sauces : +5,00 € par plat.
                    </div>
                  )}
                </div>
              )}
              {suggestions.needs_accompagnement && (
                <div>
                  <p className="text-xs font-semibold text-gray-600 mb-2 flex items-center gap-1"><UtensilsCrossed size={14} className="text-green-500" /> Accompagnements</p>
                  <div className="flex flex-wrap gap-2">
                    {suggestions.accompagnement_options?.map(opt => {
                      const optName = typeof opt === 'object' ? opt.name : opt;
                      const optPrix = typeof opt === 'object' ? opt.prix : 0;
                      const isSelected = selectedAccomp.some(a => (typeof a === 'object' ? a.name : a) === optName);
                      return (
                        <button key={optName} onClick={() => setSelectedAccomp(prev => {
                          const has = prev.some(a => (typeof a === 'object' ? a.name : a) === optName);
                          return has ? prev.filter(a => (typeof a === 'object' ? a.name : a) !== optName) : [...prev, optPrix > 0 ? { name: optName, prix: optPrix } : optName];
                        })}
                          className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${isSelected ? 'bg-green-500 text-white border-green-500' : 'bg-white text-gray-700 border-gray-200 hover:border-green-300'}`}>
                          {optName}{optPrix > 0 && <span className="ml-1 text-[10px]">+{optPrix.toFixed(2)}&euro;</span>}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-[10px] text-gray-500 mt-1">
                    {suggestions.included_accompaniments ?? 2} accompagnements inclus.
                  </p>
                  {selectedAccomp.length > (suggestions.included_accompaniments ?? 2) && (
                    <div className="mt-2 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">
                      Supplément accompagnements :
                      +{((selectedAccomp.length - (suggestions.included_accompaniments ?? 2)) * (suggestions.extra_accompaniment_fee ?? 5)).toFixed(2)} €.
                    </div>
                  )}
                </div>
              )}
              <Button onClick={confirmOptions} className="w-full bg-[#8B0000] hover:bg-[#6d0000] text-white" data-testid="confirm-options-btn">
                Ajouter au panier
              </Button>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Wine Color Picker Dialog */}
      <Dialog open={showWinePicker} onOpenChange={o => { if (!o) { setShowWinePicker(false); setPendingWineItem(null); } }}>
        <DialogContent className="sm:max-w-xs" data-testid="wine-color-picker">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2"><Wine size={18} className="text-[#8B0000]" /> {pendingWineItem?.name}</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-gray-500 mb-3">Choisissez la couleur du vin :</p>
          <div className="flex flex-col gap-2">
            {[
              { label: 'Rouge', color: 'bg-red-600 hover:bg-red-700 text-white' },
              { label: 'Rose', color: 'bg-pink-400 hover:bg-pink-500 text-white' },
              { label: 'Blanc', color: 'bg-amber-100 hover:bg-amber-200 text-amber-900 border border-amber-300' },
            ].map(opt => (
              <button key={opt.label} data-testid={`wine-${opt.label.toLowerCase()}`}
                onClick={() => {
                  if (pendingWineItem) {
                    const modified = { ...pendingWineItem, name: `${pendingWineItem.name} - ${opt.label}` };
                    addToCartDirect(modified, null, null, []);
                  }
                  setShowWinePicker(false);
                  setPendingWineItem(null);
                }}
                className={`py-3 rounded-lg font-bold text-sm transition-all ${opt.color}`}>
                {opt.label}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StaffOrder;
